import { AnyAction } from '@reduxjs/toolkit';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { TWorkType } from 'data/types/workTypes.types';
import { timeEntryApi } from 'redux/apiSlices/timeEntry.slice';
import { categoriesApi } from 'redux/apiSlices/category.slice';

export const WORK_TYPE_URL = 'api/work-type';

// Work types are read through two other createApi instances, which this one's tags cannot
// reach: timeEntryApi serves the dropdowns (timesheet, reports, contracts, bulk invoice,
// entry filters) and categoriesApi serves Category Settings plus this page's own
// "No category" badge. Refresh both by hand once a write succeeds, or a rename keeps
// showing the old name and a retired type keeps appearing in the pickers.
const invalidateWorkTypeConsumersOnSuccess = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: AnyAction) => void;
    queryFulfilled: PromiseLike<unknown>;
  },
) => {
  try {
    await queryFulfilled;
    dispatch(timeEntryApi.util.invalidateTags(['workTypes']));
    dispatch(categoriesApi.util.invalidateTags(['workTypes']));
  } catch {
    // The write failed, so the catalog those two read is unchanged.
  }
};

export const workTypeApi = authSplitApi('workTypeApi', [
  'workTypeCatalog',
]).injectEndpoints({
  endpoints: build => ({
    // the full catalog, retired entries included — the management page needs to show both
    getWorkTypeCatalog: build.query<TWorkType[], void>({
      query: () => ({
        url: `${WORK_TYPE_URL}/all`,
        method: 'GET',
      }),
      providesTags: ['workTypeCatalog'],
    }),
    createWorkType: build.mutation<TWorkType, { name: string }>({
      query: body => ({
        url: WORK_TYPE_URL,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['workTypeCatalog'],
      onQueryStarted: invalidateWorkTypeConsumersOnSuccess,
    }),
    renameWorkType: build.mutation<TWorkType, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `${WORK_TYPE_URL}/${id}/name`,
        method: 'PATCH',
        body: { value: name },
      }),
      invalidatesTags: ['workTypeCatalog'],
      onQueryStarted: invalidateWorkTypeConsumersOnSuccess,
    }),
    updateWorkTypeStatus: build.mutation<
      TWorkType,
      { id: string; isActive: boolean }
    >({
      query: ({ id, isActive }) => ({
        url: `${WORK_TYPE_URL}/${id}/active`,
        method: 'PATCH',
        body: { value: isActive },
      }),
      invalidatesTags: ['workTypeCatalog'],
      onQueryStarted: invalidateWorkTypeConsumersOnSuccess,
    }),
  }),
});

export const {
  useGetWorkTypeCatalogQuery,
  useCreateWorkTypeMutation,
  useRenameWorkTypeMutation,
  useUpdateWorkTypeStatusMutation,
} = workTypeApi;
